"use client"

import { useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { getUserSession, terminateAllOtherSessions, terminateSession, performLogout } from "../redux/slices/authSlice"
import { showToast } from "../redux/slices/toastSlice"

const MAX_ALLOWED_DEVICES = 2

const SessionConflictScreen = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const sessions = useSelector((state) => state.auth.sessions || [])
  const isLoading = useSelector((state) => state.auth.isLoading)
  const [isTerminating, setIsTerminating] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    // Fetch sessions on component mount
    dispatch(getUserSession())
  }, [dispatch])

  // Prevent navigation away from session-conflict route
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = ""
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    // Set a flag in localStorage to indicate user is blocked
    localStorage.setItem("session_conflict_blocked", "true")

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      localStorage.removeItem("session_conflict_blocked")
    }
  }, [])

  const handleTerminateOthers = async () => {
    setIsTerminating(true)
    setError("")

    try {
      const result = await dispatch(terminateAllOtherSessions())

      if (terminateAllOtherSessions.fulfilled.match(result)) {
        // Clear the blocked flag before redirecting
        localStorage.removeItem("session_conflict_blocked")
        // Show welcome toast
        dispatch(showToast({ msg: "Welcome! You can now access your account.", state: "success" }))
        // Success - redirect to dashboard
        navigate("/dashboard", { replace: true })
      } else {
        setError("Failed to terminate other sessions. Please try again.")
        setIsTerminating(false)
      }
    } catch (err) {
      setError("An error occurred. Please try again.")
      setIsTerminating(false)
    }
  }

  const handleTerminateSingle = async (sessionId) => {
    try {
      const result = await dispatch(terminateSession(sessionId))
      if (terminateSession.fulfilled.match(result)) {
        // Clear the blocked flag and redirect to dashboard
        // Show welcome toast
        dispatch(showToast({ msg: "Welcome! You can now access your account.", state: "success" }))
        localStorage.removeItem("session_conflict_blocked")
        navigate("/dashboard", { replace: true })
      } else {
        setError("Failed to terminate session. Please try again.")
      }
    } catch (err) {
      setError("An error occurred. Please try again.")
    }
  }

  const handleLogoutThisDevice = async () => {
    setIsLoggingOut(true)
    setError("")

    try {
      // Clear the blocked flag before redirecting
      localStorage.removeItem("session_conflict_blocked")
      await dispatch(performLogout())
      navigate("/login", { replace: true })
    } catch (err) {
      setError("Failed to logout. Please try again.")
      setIsLoggingOut(false)
    }
  }

  const renderUserAgent = (userAgent) => {
    let browser = "Unknown Browser"
    let os = "Unknown OS"

    if (/Chrome/i.test(userAgent)) browser = "Chrome"
    else if (/Firefox/i.test(userAgent)) browser = "Firefox"
    else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) browser = "Safari"
    else if (/Edge/i.test(userAgent)) browser = "Edge"

    if (/Windows/i.test(userAgent)) os = "Windows"
    else if (/Mac OS|Macintosh|MacOS/i.test(userAgent)) os = "MacOS"
    else if (/Linux/i.test(userAgent)) os = "Linux"
    else if (/Android/i.test(userAgent)) os = "Android"
    else if (/iPhone|iPad|iOS/i.test(userAgent)) os = "iOS"

    return `${browser} on ${os}`
  }

  const getDeviceIcon = (device) => {
    if (device.includes("iPhone") || device.includes("Android")) {
      return "📱"
    } else if (device.includes("MacOS")) {
      return "💻"
    } else if (device.includes("Windows")) {
      return "🖥️"
    } else if (device.includes("Linux")) {
      return "🐧"
    }
    return "🌐"
  }

  const currentSession = sessions.find(s => s.isCurrent)
  const otherSessions = sessions.filter(s => !s.isCurrent)

  return (
    <>
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-body-tertiary p-4">
        <div className="card border-0" style={{ maxWidth: '600px', width: '100%' }}>
          <div className="card-body p-5">
            {/* Header with warning */}
            <div className="text-center mb-4">
              <h2 className="mb-2 fw-bold">Access Blocked</h2>
              <p className="text-danger fw-semibold mb-0">
                Maximum {MAX_ALLOWED_DEVICES} device{MAX_ALLOWED_DEVICES > 1 ? 's' : ''} allowed
              </p>
            </div>

            {/* Error message */}
            {error && (
              <div className="alert alert-danger mb-4" role="alert">
                {error}
              </div>
            )}

            {/* Session info */}
            <div className="mb-4">
              <p className="text-muted mb-4">
                You are currently logged in on <strong>{sessions.length}</strong> device{sessions.length > 1 ? 's' : ''}. To continue, you must either terminate other sessions or logout from this device.
              </p>

              {/* Current session */}
              {currentSession && (
                <div className="card mb-3 border-success border-2 bg-body-tertiary">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center gap-3 mb-2">
                      <span className="fs-3">{getDeviceIcon(currentSession.device)}</span>
                      <div className="flex-grow-1">
                        <div className="fw-bold mb-1">{renderUserAgent(currentSession.device)}</div>
                        <div className="text-muted">
                          {currentSession.location?.country} {currentSession.location?.city} • 🌐 IP: {currentSession.ipAddress}
                        </div>
                      </div>
                      <div className="">
                        <span className="badge bg-success mt-1">Current Session</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Other sessions */}
              {otherSessions.length > 0 && (
                <div className="mb-3">
                  <div className="fw-bold mb-1 text-body">Other Active Sessions:</div>
                  {otherSessions.map((session, index) => (
                    <div key={session.id || index} className="card mb-2 border bg-body">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-start gap-3 mb-2">
                          <span className=" fs-3">{getDeviceIcon(session.device)}</span>
                          <div className="flex-grow-1">
                            <div className="fw-bold mb-1">{renderUserAgent(session.device)}</div>
                            <div className="text-muted">
                              {session.location?.country} {session.location?.city} • 🌐 IP: {session.ipAddress}
                            </div>
                            <div className="text-muted mt-1">
                              Last active: {new Date(session.lastActiveAt).toLocaleString()}
                            </div>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-danger w-auto"
                            onClick={() => handleTerminateSingle(session.id)}
                          >
                            Terminate
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="d-grid gap-2">
              <button
                className="btn btn-danger fw-bold py-3"
                onClick={handleTerminateOthers}
                disabled={isTerminating || isLoading || otherSessions.length === 0}
              >
                {isTerminating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Terminating All...
                  </>
                ) : (
                  "Terminate All Other Sessions"
                )}
              </button>

              <button
                className="btn btn-outline-secondary py-3 fw-medium"
                onClick={handleLogoutThisDevice}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Logging out...
                  </>
                ) : (
                  "Logout from This Device"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SessionConflictScreen
